import { useAuthStore } from '../../stores/auth-store';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { useForm } from 'react-hook-form';
import { useUpdateUser } from '../../hooks/use-users';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';
import type { WebConClient } from '../../types';

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();
  const updateUser = useUpdateUser();

  const { data: clientData, isLoading: clientLoading } = useQuery({
    queryKey: ['clients', 'me'],
    queryFn: () => api.get<WebConClient>('/clients/me'),
    enabled: user?.role === 'CLIENT',
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const client = clientData?.data;

  const { register, handleSubmit } = useForm({
    defaultValues: { name: user?.name ?? '' },
  });

  const onSubmit = async (data: { name: string }) => {
    if (!user) return;
    try {
      await updateUser.mutateAsync({ id: user.id, name: data.name });
      toast('success', 'Profilul a fost salvat cu succes');
    } catch (e) {
      toast('error', `Eroare la salvare: ${(e as Error).message}`);
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <h1 className="mb-6 text-2xl font-bold text-gray-900 dark:text-dark-text">Profilul Meu</h1>
      <Card>
        <CardHeader><h2 className="font-semibold">Informații cont</h2></CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input label="Email" value={user?.email ?? ''} disabled />
            <Input {...register('name')} label="Nume" disabled={user?.role === 'CLIENT'} />
            {user?.role === 'CLIENT' && (
              <p className="text-xs text-gray-500 dark:text-dark-muted">Numele poate fi modificat doar de un administrator.</p>
            )}
            <Input label="Rol" value={user?.role ?? ''} disabled />
            {user?.role !== 'CLIENT' && (
            <div className="flex justify-end">
              <Button type="submit" disabled={updateUser.isPending}>Salvează</Button>
            </div>
            )}
          </form>
        </CardContent>
      </Card>

      {user?.role === 'CLIENT' && (
        <Card>
          <CardHeader><h2 className="font-semibold">Informații companie</h2></CardHeader>
          <CardContent>
            {clientLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-5 w-64" />
                <Skeleton className="h-5 w-40" />
              </div>
            ) : client ? (
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-dark-muted">Companie</dt>
                  <dd className="font-medium text-gray-900 dark:text-dark-text">{client.name}</dd>
                </div>
                {client.clientCode && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-dark-muted">Cod client</dt>
                    <dd className="font-medium text-gray-900 dark:text-dark-text">{client.clientCode}</dd>
                  </div>
                )}
                {client.vatNumber && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-dark-muted">CUI</dt>
                    <dd className="font-medium text-gray-900 dark:text-dark-text">{client.vatNumber}</dd>
                  </div>
                )}
                {client.contactEmail && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-dark-muted">Email contact</dt>
                    <dd className="font-medium text-gray-900 dark:text-dark-text">{client.contactEmail}</dd>
                  </div>
                )}
                {client.phone && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-dark-muted">Telefon</dt>
                    <dd className="font-medium text-gray-900 dark:text-dark-text">{client.phone}</dd>
                  </div>
                )}
                {client.address && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-dark-muted">Adresă</dt>
                    <dd className="font-medium text-gray-900 dark:text-dark-text">{client.address}{client.city ? `, ${client.city}` : ''}</dd>
                  </div>
                )}
                {client.supportManager && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-dark-muted">Manager suport</dt>
                    <dd className="font-medium text-gray-900 dark:text-dark-text">{client.supportManager}</dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-sm text-gray-400">Datele companiei nu sunt disponibile.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* SLA Card */}
      {user?.role === 'CLIENT' && client?.sla && (client.sla.criticalResponseTime || client.sla.normalResponseTime || client.sla.esteticResponseTime) && (
        <Card>
          <CardHeader><h2 className="font-semibold">SLA — Timpi de răspuns și rezolvare</h2></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 dark:text-dark-muted dark:border-dark-border">
                    <th className="pb-2 font-medium">Prioritate</th>
                    <th className="pb-2 font-medium">Răspuns (ore)</th>
                    <th className="pb-2 font-medium">Rezolvare (ore)</th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-dark-border">
                  {client.sla.criticalResponseTime && (
                    <tr>
                      <td className="py-2 font-medium text-red-700 dark:text-red-400">Critic</td>
                      <td className="py-2">{client.sla.criticalResponseTime}</td>
                      <td className="py-2">{client.sla.criticalSolvingTime || '—'}</td>
                    </tr>
                  )}
                  {client.sla.normalResponseTime && (
                    <tr>
                      <td className="py-2 font-medium text-blue-700 dark:text-blue-400">Normal</td>
                      <td className="py-2">{client.sla.normalResponseTime}</td>
                      <td className="py-2">{client.sla.normalSolvingTime || '—'}</td>
                    </tr>
                  )}
                  {client.sla.esteticResponseTime && (
                    <tr>
                      <td className="py-2 font-medium text-gray-600 dark:text-gray-400">Estetic</td>
                      <td className="py-2">{client.sla.esteticResponseTime}</td>
                      <td className="py-2">{client.sla.esteticSolvingTime || '—'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
